import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { FramerTooltip, RoutedByIndicatorContent } from "@ryde/controls";
import { useRideHubInfo, useRideLink } from "@ryde/redux";
import {
  collectRelatedHubRideIdsBySegment,
  editFormDataAF,
  getJoinedProductionIds,
  IRideDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  resolveDirectionsServiceLabel,
  RideCrudModeKeyTuple,
  useDrawers,
  useHasPermissions,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { useRideRebuildRouteAppliedInfo } from "./hooks";
import {
  RideAreaGroupSupplierSourceIndicator,
  RideDetourIndicator,
  RideHadDetourIndicator,
  RideSearchRouteIterationIndicator,
} from "./indicators";
import { RideOpenStationRouteLink } from "./ride-open-station-route-link";
import { RideTabsBottomBlocksExpandPanelStyled } from "./ride-tabs-bottom-blocks.styled";
import { RideTabsBottomBookings } from "./ride-tabs-bottom-bookings";
import { RideTabsBottomSites } from "./ride-tabs-bottom-sites";
import { RideBottomBlockStyled } from "./ride.styled";

const RideRelatedHubIdLink: FC<{ rideId: number }> = ({ rideId }) => {
  const { link } = useRideLink(rideId);
  if (link) {
    return (
      <Link
        to={link}
        target="_blank"
        rel="noopener noreferrer"
        className="ride-header-related-hub-link"
      >
        {rideId}
      </Link>
    );
  }
  return <span className="ride-header-related-hub-id">{rideId}</span>;
};

export const RideTabsBottomBlocksDetails: FC = () => {
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );

  const { productionId } = rideDto ?? {};

  const { canOpenProductionDrawer, hasReadStationRoutePermission } =
    useHaveCustomPermissions();

  const { hasPermissions: hasReadBookingPermission } = useHasPermissions({
    entity: "booking",
    action: "read",
  });

  const { hasPermissions: hasViewRideSupplierPermission } = useHasPermissions({
    entity: "ride",
    action: "view_ride_supplier",
  });

  const { openEditFormDrawer } = useDrawers();
  const { t } = useTranslation();
  const { persistedRebuildDetailText } = useRideRebuildRouteAppliedInfo();

  const { link: duplicatedRideLink } = useRideLink(
    rideDto?.metadata?.duplicatedFromRide,
  );
  const { link: revertedRideLink } = useRideLink(
    rideDto?.metadata?.returnOfRide,
  );

  const handleProductionClick = (): void => {
    if (canOpenProductionDrawer && productionId) {
      openEditFormDrawer({
        entity: "rideProduction",
        id: productionId,
      });
    }
  };

  const handleJoinedProductionClick = (joinedProductionId: number): void => {
    if (canOpenProductionDrawer) {
      openEditFormDrawer({
        entity: "rideProduction",
        id: joinedProductionId,
      });
    }
  };

  const joinedProductionIds = getJoinedProductionIds(rideDto);

  const hubInfo = useRideHubInfo(rideDto);
  const { relatedMainRideIds, relatedPersonalRideIds } = useMemo(
    () => collectRelatedHubRideIdsBySegment(rideDto),
    [rideDto],
  );
  const excludedStationRouteIds = rideDto?.metadata?.excludedStationRouteIds;
  const stationRouteId =
    rideDto?.metadata?.usedCachedRouteId ||
    rideDto?.metadata?.customStationRouteId;
  const routedByLabel = resolveDirectionsServiceLabel(
    rideDto?.metadata?.directionsService,
  );

  return (
    <RideTabsBottomBlocksExpandPanelStyled>
      {!!rideDto?.metadata?.duplicatedFromRide && !!duplicatedRideLink && (
        <Link to={duplicatedRideLink} target="_blank" rel="noopener noreferrer">
          <RideBottomBlockStyled className="duplication-source-ride">
            <FramerTooltip
              content={t("ride.DUPLICATED_FROM_RIDE", {
                ride: rideDto?.metadata?.duplicatedFromRide,
              })}
              rtlStrippedBottomLeftPlacement
            >
              <label>
                {t("ride.DUPLICATED_FROM_RIDE", {
                  ride: rideDto?.metadata?.duplicatedFromRide,
                })}
              </label>
            </FramerTooltip>
          </RideBottomBlockStyled>
        </Link>
      )}

      {!!rideDto?.metadata?.returnOfRide && !!revertedRideLink && (
        <Link to={revertedRideLink} target="_blank" rel="noopener noreferrer">
          <RideBottomBlockStyled className="return-of-ride">
            <FramerTooltip
              content={t("ride.RETURN_OF_RIDE", {
                ride: rideDto?.metadata?.returnOfRide,
              })}
              rtlStrippedBottomLeftPlacement
            >
              <label>
                {t("ride.RETURN_OF_RIDE", {
                  ride: rideDto?.metadata?.returnOfRide,
                })}
              </label>
            </FramerTooltip>
          </RideBottomBlockStyled>
        </Link>
      )}

      {!rideDto?.metadata?.duplicatedFromRide && (
        <RideBottomBlockStyled
          className={clsx({
            pointer: canOpenProductionDrawer,
          })}
          onClick={handleProductionClick}
        >
          <label>{t("general.PRODUCTION")}</label>
          <span>{rideDto?.productionId || NO_VALUE_DISPLAY}</span>
        </RideBottomBlockStyled>
      )}
      {joinedProductionIds.length > 0 && (
        <RideBottomBlockStyled>
          <label>{t("general.JOIN")}</label>
          <span>
            {joinedProductionIds.map((id, index) => (
              <span key={id}>
                <span
                  className={clsx({
                    pointer: canOpenProductionDrawer,
                  })}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleJoinedProductionClick(id)}
                  onKeyDown={onActivateKeyDown(() =>
                    handleJoinedProductionClick(id),
                  )}
                >
                  {id}
                </span>
                {index < joinedProductionIds.length - 1 && ", "}
              </span>
            ))}
          </span>
        </RideBottomBlockStyled>
      )}

      {hubInfo && relatedMainRideIds.length > 0 && (
        <RideBottomBlockStyled>
          <label>{t("ride.HUB_MAIN")}</label>
          <span>
            {relatedMainRideIds.map((id, index) => (
              <span key={id}>
                <RideRelatedHubIdLink rideId={id} />
                {index < relatedMainRideIds.length - 1 && ", "}
              </span>
            ))}
          </span>
        </RideBottomBlockStyled>
      )}
      {hubInfo && relatedPersonalRideIds.length > 0 && (
        <RideBottomBlockStyled>
          <label>{t("ride.HUB_PERSONAL")}</label>
          <span>
            {relatedPersonalRideIds.map((id, index) => (
              <span key={id}>
                <RideRelatedHubIdLink rideId={id} />
                {index < relatedPersonalRideIds.length - 1 && ", "}
              </span>
            ))}
          </span>
        </RideBottomBlockStyled>
      )}

      {!!hasReadStationRoutePermission && !!excludedStationRouteIds?.length && (
        <RideBottomBlockStyled>
          <label>{t("ride.formLabel.excludedStationRoutesIds")}</label>
          <RideOpenStationRouteLink
            stationRouteIds={excludedStationRouteIds}
            rideDto={rideDto}
          />
        </RideBottomBlockStyled>
      )}
      {!!hasReadStationRoutePermission && !!stationRouteId && (
        <RideBottomBlockStyled>
          <label>{t("stationRoute.single")}</label>
          <RideOpenStationRouteLink
            stationRouteIds={[stationRouteId]}
            rideDto={rideDto}
          />
        </RideBottomBlockStyled>
      )}
      <RideSearchRouteIterationIndicator rideDto={rideDto} />
      {!!routedByLabel && (
        <RideBottomBlockStyled>
          <RoutedByIndicatorContent
            directionsService={rideDto?.metadata?.directionsService}
          />
        </RideBottomBlockStyled>
      )}
      {!!persistedRebuildDetailText && (
        <RideBottomBlockStyled data-e2e="ride-last-rebuild-indicator">
          <label>{t("ride.LAST_REBUILD_TYPE_APPLIED")}</label>
          <span>{persistedRebuildDetailText}</span>
        </RideBottomBlockStyled>
      )}
      {!!hasViewRideSupplierPermission && (
        <RideAreaGroupSupplierSourceIndicator rideDto={rideDto} />
      )}
      <RideDetourIndicator rideDto={rideDto} />
      <RideHadDetourIndicator rideDto={rideDto} />
      <RideTabsBottomSites />
      {!!hasReadBookingPermission && <RideTabsBottomBookings />}
    </RideTabsBottomBlocksExpandPanelStyled>
  );
};
